export interface AnalyticsEventDefinition {
  description: string;
  requiredProperties?: string[];
  funnelCritical?: boolean;
}

export const REQUIRED_FUNNEL_PROPERTIES = [
  'flow',
  'step',
  'step_index',
  'outcome',
  'correlation_id',
  'client_id',
  'partner_id',
  'user_type',
  'platform',
  'session_id',
  'timestamp_iso',
] as const;

export const ANALYTICS_EVENT_CATALOG: Record<string, AnalyticsEventDefinition> =
  {
    page_viewed: {
      description:
        'Canonical page view event for unified tracking of page visits across the app',
    },
    funnel_step_viewed: {
      description:
        'Generic funnel step event for standardized flow step tracking',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    search_started: {
      description: 'User starts or confirms a search journey',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    results_loaded: {
      description:
        'Search results loaded successfully or with empty/error outcomes',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    item_selected: {
      description: 'User selects an item from result set',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    checkout_started: {
      description: 'User enters review/checkout flow',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    payment_attempted: {
      description: 'User starts payment attempt',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    payment_success: {
      description:
        'Stripe payment sheet returned Completed. NON-FUNNEL sub-event of the payment step. The funnel uses payment_attempted as the canonical step event; payment_success / payment_failed / payment_cancelled describe outcomes within that step. To measure "users who paid successfully", query payment_success directly.',
    },
    booking_initiated: {
      description:
        'App called the booking-confirmation API after payment (booking step, after payment): step 6 in non-flight flows, step 7 in flight. Fires before the API responds; pair with booking_submitted (async ACK) and booking_completed/booking_failed (final) to measure backend processing.',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    purchase_initiated: {
      description:
        'App called the purchase-verification API after payment (purchase step, non-booking flows like membership/service-list/service-details). Mirrors booking_initiated.',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    booking_submitted: {
      description:
        'NON-FUNNEL sub-event of the booking step. Fires when the booking API returns an async ACK / "processing" status (common in flight ticketing — the request is accepted but final confirmation comes later via polling or webhook). User can drop off here and the booking still completes server-side. Distinct outcome of the same booking step as booking_initiated.',
    },
    prebook_initiated: {
      description:
        'Flight-specific: createFlightBookingRequest API called (step 5, between checkout_started and payment_attempted). This API performs price validation and seat reservation before payment. Flight v2 is a 9-step funnel: 1 search → 2 results → 3 details → 4 review → 5 prebook → 6 payment → 7 payment_success → 8 booking_initiated → 9 confirmation.',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    prebook_validated: {
      description:
        'Flight-specific: createFlightBookingRequest responded with success — price valid, seats reserved, paymentData received. Pair with prebook_initiated to measure backend prebook latency and success rate.',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    prebook_failed: {
      description:
        'Flight-specific: createFlightBookingRequest responded with error. Carries `reason`: price_changed | fare_expired | seats_unavailable | api_error_<code>. The biggest in-funnel drop-off point in flight bookings — visible failure modes.',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    addons_modal_opened: {
      description: 'User opened the flight addons modal (seats/meals/baggage)',
    },
    addons_modal_dismissed: {
      description: 'User closed the flight addons modal without applying',
    },
    addons_applied: {
      description:
        'User confirmed addon selections (Apply / Continue). Carries totals + counts of selected items by type.',
    },
    addon_seat_selected: {
      description: 'Flight: user added/changed a seat selection in the addons modal',
    },
    addon_meal_selected: {
      description: 'Flight: user added a meal in the addons modal',
    },
    addon_baggage_selected: {
      description: 'Flight: user added extra baggage in the addons modal',
    },
    addons_tab_changed: {
      description:
        'Flight addons modal: user switched between Seats / Meals / Baggage tabs',
    },
    addons_segment_changed: {
      description:
        'Flight addons modal: user switched the meal segment or baggage route (departure/arrival)',
    },
    rooms_view_opened: {
      description:
        'Hotel: user tapped "Select Room" on the details page, opening the rooms modal. Distinct from checkout_continue_clicked (which is intent to proceed AFTER a room is picked) and room_selection_opened (the modal-mount diagnostic).',
    },
    purchase_completed: {
      description:
        'Generic purchase/payment success for non-booking purchase flows',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    purchase_failed: {
      description:
        'Generic purchase/payment failure for non-booking purchase flows',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    booking_completed: {
      description: 'Booking completion success',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    booking_failed: {
      description: 'Booking completion failed',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },
    request_submitted: {
      description:
        'Service-request submission (used for non-booking forms like baggage insurance, meet & greet, membership requests)',
      funnelCritical: true,
      requiredProperties: [...REQUIRED_FUNNEL_PROPERTIES],
    },

    product_selected: {
      description:
        'User opened/viewed an individual product detail page (e.g. duty-free product) after selecting a store. NON-FUNNEL sub-event nested under the item_selected (store) step — distinct from item_selected which marks store/service-level selection.',
    },
    request_initiated: {
      description:
        'User submitted a pre-payment checkout/details form (intent signal). NON-FUNNEL — precedes payment_attempted. Distinct from request_submitted, which is the terminal step-7 ack/result of the request.',
    },

    // Non-funnel canonical events. Listed so callsites can be validated and
    // dashboards can discover them, but no required-property contract.
    results_filter_opened: { description: 'User opened the filter UI' },
    results_filter_changed: { description: 'User applied or modified filters' },
    results_filter_dismissed: { description: 'Filter UI dismissed without applying' },
    results_sort_opened: { description: 'User opened the sort UI' },
    results_sort_changed: { description: 'User applied or modified sorting' },
    results_sort_dismissed: { description: 'Sort UI dismissed without applying' },
    results_pagination_triggered: {
      description: 'User triggered pagination (e.g., infinite scroll, next page)',
    },
    results_pagination_loaded: {
      description: 'Pagination response completed, success or empty',
    },
    results_page_left: { description: 'User left a results page (back/exit)' },
    search_navigation_clicked: {
      description: 'User clicked navigation control on search results',
    },
    checkout_user_details_submitted: {
      description: 'User submitted personal/contact details in checkout',
    },
    checkout_continue_clicked: {
      description:
        'User indicated intent to start checkout on a details page (precedes canonical checkout_started which fires on review-page load)',
    },
    checkout_back_clicked: { description: 'User navigated back from checkout' },
    checkout_error: { description: 'Error encountered during checkout' },
    checkout_offer_selected: {
      description: 'User explicitly selected an offer in checkout',
    },
    checkout_offer_auto_applied: {
      description:
        'Offer auto-applied by the system (e.g., default/best offer on review-page load). Distinct from checkout_offer_selected which is user-initiated.',
    },
    saved_guest_list_opened: {
      description:
        'User opened the saved-guest picker on a booking review page',
    },
    saved_guest_selected: {
      description:
        'User picked a guest from the saved-guest picker. Carries selected_guest_id.',
    },
    saved_guest_list_dismissed: {
      description: 'User dismissed the saved-guest picker without selecting',
    },
    payment_cancelled: {
      description:
        'User abandoned the payment sheet without entering card details (Stripe Canceled). NOT a failure — distinct from booking_failed/purchase_failed which represent actual payment processing errors.',
    },
    payment_failed: {
      description:
        'In-flight payment attempt failed (Stripe Failed) — payment processing error mid-flow. The journey is NOT terminated; user can retry payment within the same correlation_id. Distinct from booking_failed/purchase_failed which signal that the booking definitively did not happen.',
    },
    booking_cancel_attempted: {
      description:
        'User confirmed the "are you sure?" booking-cancellation modal. Fires BEFORE the cancellation API call. Post-booking lifecycle event, not part of the search→book funnel.',
    },
    booking_cancel_confirmed: {
      description:
        'Backend confirmed booking cancellation. Fires AFTER the cancellation API succeeds. Post-booking lifecycle event.',
    },
    booking_cancel_failed: {
      description:
        'Cancellation API errored. Post-booking lifecycle event.',
    },
    booking_cancel_dismissed: {
      description: 'User dismissed the cancel-booking modal without confirming',
    },
    search_input_selected: {
      description:
        'User selected an autocomplete suggestion in a search input (e.g., city). Distinct from search_started which fires on the actual Search-button click.',
    },
    search_results_page_loaded: {
      description:
        'Search results page has mounted (diagnostic). Distinct from results_loaded, which is the funnel-critical "results API responded" event.',
    },
    tour_card_clicked: {
      description:
        'Tours & Experiences: user tapped a result card to view details (NON-FUNNEL browsing intent — distinct from item_selected, which fires when the user confirms a ticket/variant selection).',
    },
    availability_checked: {
      description:
        'Tours & Experiences: availability API responded for the selected date/variants (NON-FUNNEL). outcome=success when a bookingReference is returned, error otherwise (no availability or API failure).',
    },
    esim_product_selected: {
      description:
        'eSIM: user picked a country/product in the product-search modal (NON-FUNNEL intent signal — distinct from item_selected, which fires when the user lands on the booking-review page for that product).',
    },
    esim_plan_selected: {
      description: 'eSIM: user selected a data plan on the booking-review page (NON-FUNNEL)',
    },
    esim_variant_selected: {
      description: 'eSIM: user selected a plan variant on the booking-review page (NON-FUNNEL)',
    },
    user_login: { description: 'User logged in' },
    login_failed: { description: 'Login attempt failed' },
    user_registered: { description: 'New user registered' },
    user_logout: { description: 'User logged out' },
  };
